import { CaseStatus } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import ApplicationFieldReviews from '@platform-ui/case/ApplicationFieldReviews/ApplicationFieldReviews.js';
import CaseDocuments from '@platform-ui/case/CaseDocuments/CaseDocuments.js';
import useCase from '@platform-ui/case/hooks/useCase.js';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { Box, Flex, Heading } from '@radix-ui/themes';
import { IKImage } from 'imagekitio-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { isThirdPartyUser } from '@platform-ui/common/utilities/applicantType.js';
import ActivityFeedPanel from './ActivityFeedPanel/index.js';
import ApplicationPanel from './ApplicationPanel.js';
import styles from './CaseDetails.module.css';
import CaseStatusPanel from './CaseStatusPanel/index.js';
import FulfillmentsPanel from './FulfillmentsPanel.js';

export default function CaseDetails(): JSX.Element {
  const location = useLocation();
  const activityPanelRef = useRef(null);
  const history = useHistory();
  const userHomeRoute = useUserHomeRoute();
  const program = useProgram();
  const posthog = usePostHog();
  const { case_ } = useCase();
  const { user } = useAuth();
  const isThirdParty = isThirdPartyUser(user?.core);
  const heading = `${program?.name} Case Details`;
  const { showSnackbar } = useSnackbar();
  useCustomPageTitle(heading);

  useEffect(() => {
    if (!case_ || !program) history.push(userHomeRoute);
  }, [case_, program, history, userHomeRoute]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('submit_success') === 'true') {
      showSnackbar('Successfully submitted!');
    }
  }, [location.search, showSnackbar]);

  if (!case_ || !program) return null;

  useEffect(() => {
    if (location?.hash.includes('#activity-section'))
      activityPanelRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [location]);

  return (
    <Flex direction="column" align="center">
      <Flex direction="column" align="center" mb="5">
        <Box maxHeight="8rem" mb="4">
          <IKImage
            src={program.heroImage}
            alt={`${program.name} logo`}
            className={`${'max-h-full h-fill-available'} ${styles.CaseDetailsHeroImage}`}
            urlEndpoint="https://ik.imagekit.io/bybeam/"
          />
        </Box>
        <Heading size="6">{heading}</Heading>
      </Flex>
      <Flex maxWidth="48rem" direction="column" gap="4" align="center" width="100%">
        {isThirdParty ? (
          <Flex
            direction="column"
            gap="2"
            px="6"
            py="4"
            width="100%"
            style={{ backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-3)' }}
          >
            <Heading size="5" weight="regular">
              Case Overview
            </Heading>
            <Flex direction="column" gap="2">
              <Box>
                <strong>Case ID:</strong> {case_.displayId}
              </Box>
              <Box>
                <strong>Primary Applicant:</strong> {case_.name}
              </Box>
            </Flex>
          </Flex>
        ) : (
          <CaseStatusPanel />
        )}
        {!isThirdParty &&
          posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.resubmissionRequests) &&
          case_.status === CaseStatus.Incomplete &&
          case_.applications &&
          case_.applications.length > 0 && (
            <ApplicationFieldReviews applicationId={case_.applications[0]?.id} />
          )}
        {posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.caseWorkflowImprovements) && (
          <CaseDocuments caseId={case_.id} />
        )}
        <span ref={activityPanelRef} className="w-full">
          <ActivityFeedPanel />
        </span>
        <ApplicationPanel />
        <FulfillmentsPanel />
        {!isThirdParty && (
          <Button link={ApplicantRoutes.APPLICATION_PORTAL}>Back To Application Portal</Button>
        )}
      </Flex>
    </Flex>
  );
}
